import React, { useEffect, useRef, useState } from 'react';
import {
  getIllustrationAssetUrl,
  isEagerIllustrationContext,
  resolveIllustrationContext,
} from '../../config/illustrations';
import { getIllustrationForPage } from '../../config/pageIllustrations';

export type IllustrationSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | number | string;

export interface IllustrationProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Registry context key, e.g. "empty.default" or "hero.home" */
  context?: string;
  /** Legacy props for backward compatibility */
  name?: string;
  category?: string;
  page?: string;
  size?: IllustrationSize;
  alt?: string;
  className?: string;
  /** Brand accent color override */
  accentColor?: string;
  /** Force loading mode */
  loading?: 'lazy' | 'eager';
}

const svgCache: Record<string, string> = {};

const SIZE_PRESETS: Record<string, string> = {
  xs: '64px',
  sm: '120px',
  md: '240px',
  lg: '360px',
  xl: '480px',
  full: '100%',
};

async function fetchSvg(url: string): Promise<string | null> {
  if (svgCache[url]) return svgCache[url];
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const text = await res.text();
    if (text.includes('<svg')) {
      svgCache[url] = text;
      return text;
    }
  } catch {
    /* network error */
  }
  return null;
}

/** Replace unDraw primary accent color dynamically */
function applyBrandAccent(raw: string, accentColor: string): string {
  return raw
    .replace(/var\(--primary-svg-color\)/gi, accentColor)
    .replace(/#6c63ff/gi, accentColor)
    .replace(/#6366f1/gi, accentColor)
    .replace(/<svg /i, '<svg width="100%" style="width:100%;height:auto;max-height:100%;display:block;" ');
}

export function Illustration({
  context,
  name,
  category,
  page,
  size = 'md',
  alt,
  className = '',
  accentColor = 'var(--color-primary)',
  loading,
  style,
  ...rest
}: IllustrationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState<boolean>(false);

  // Derive target file and alt text from context or legacy props
  let targetFile: string | null = null;
  let defaultAlt: string | undefined = undefined;

  if (context) {
    const entry = resolveIllustrationContext(context);
    if (entry) {
      targetFile = entry.file;
      defaultAlt = entry.alt;
    }
  }

  if (!targetFile && page) {
    const mapped = getIllustrationForPage(page);
    if (mapped) {
      targetFile = `${mapped.name}.svg`;
      defaultAlt = mapped.alt;
    }
  }

  if (!targetFile && name) {
    const cleanName = name.endsWith('.svg') ? name : `${name}.svg`;
    targetFile = category ? `${cleanName}` : cleanName;
  }

  if (!targetFile) {
    targetFile = 'student-learning.svg';
    defaultAlt = 'WebFoxx CUET UG & PG';
  }

  const effectiveContext = context || (page ? `page.${page.replace('/', '') || 'home'}` : 'card.course');
  const shouldEagerLoad = loading === 'eager' || (loading !== 'lazy' && isEagerIllustrationContext(effectiveContext));

  useEffect(() => {
    if (shouldEagerLoad) {
      setIsVisible(true);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldEagerLoad]);

  useEffect(() => {
    if (!isVisible || !targetFile) return;

    let mounted = true;
    setLoadState('loading');

    async function load() {
      // Priority candidate URLs
      const candidates = [
        getIllustrationAssetUrl(targetFile!),
        category ? `/assets/illustrations/${category}/${targetFile}` : null,
        `/assets/${targetFile}`,
        '/assets/illustrations/student-learning.svg',
      ].filter(Boolean) as string[];

      let loaded: string | null = null;
      for (const url of candidates) {
        loaded = await fetchSvg(url);
        if (loaded) break;
      }

      if (mounted) {
        setSvgContent(loaded ? applyBrandAccent(loaded, accentColor) : '');
        setLoadState(loaded ? 'loaded' : 'error');
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [isVisible, targetFile, category, accentColor]);

  const label = alt ?? defaultAlt;
  const isLoading = loadState === 'idle' || loadState === 'loading';

  const computedWidth = typeof size === 'number' ? `${size}px` : SIZE_PRESETS[size as string] || (size as string);
  const sizeClass = typeof size === 'string' && SIZE_PRESETS[size] ? `wfx-illustration--${size}` : '';

  const containerStyle: React.CSSProperties = {
    width: computedWidth,
    maxWidth: '100%',
    display: 'inline-block',
    verticalAlign: 'middle',
    position: 'relative',
    ...style,
  };

  return (
    <div
      ref={containerRef}
      className={`wfx-illustration vas-illustration ${sizeClass}${isLoading ? ' wfx-illustration--loading vas-loading' : ''} ${className}`.trim()}
      style={containerStyle}
      role={label && !isLoading ? 'img' : undefined}
      aria-label={label && !isLoading ? label : undefined}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {svgContent ? (
        <div dangerouslySetInnerHTML={{ __html: svgContent }} aria-hidden={label ? undefined : true} />
      ) : null}
    </div>
  );
}

export default Illustration;
